import { createSlice, PayloadAction } from '@reduxjs/toolkit';

interface SidePanelState {
  // Sites flow
  showOrderSidePanel: boolean;
  showProductSidePanel: boolean;
  showItemSidePanel: boolean;
  
  // Customers flow
  showSiteSidePanel: boolean;
  
  // Report flows
  showCustomerDetailsModal: boolean;
  
  // Order details flow
  showItemPanel: boolean;
  showItemStages: boolean;
  
  // Data states
  selectedSiteForOrders: any;
  selectedOrderForProducts: any;
  selectedProduct: any;
  selectedItem: any;
  selectedCustomerForSites: any;
  selectedProductForItems: any;
  selectedItemForStages: any;
  selectedOrder: any;
  selectedProductForConfig: any;
  selectedCustomer: any;
  selectedSite: any;
  
  // Data arrays
  siteOrders: any[];
  orderProducts: any[];
  customerSites: any[];
  
  // Loading states
  ordersLoading: boolean;
  productsLoading: boolean;
  sitesLoading: boolean;
}

const initialState: SidePanelState = {
  // Sites flow
  showOrderSidePanel: false,
  showProductSidePanel: false,
  showItemSidePanel: false,
  
  // Customers flow
  showSiteSidePanel: false,
  
  // Report flows
  showCustomerDetailsModal: false,
  
  // Order details flow
  showItemPanel: false,
  showItemStages: false,
  
  // Data states
  selectedSiteForOrders: null,
  selectedOrderForProducts: null,
  selectedProduct: null,
  selectedItem: null,
  selectedCustomerForSites: null,
  selectedProductForItems: null,
  selectedItemForStages: null,
  selectedOrder: null,
  selectedProductForConfig: null,
  selectedCustomer: null,
  selectedSite: null,
  
  // Data arrays
  siteOrders: [],
  orderProducts: [],
  customerSites: [],
  
  // Loading states
  ordersLoading: false,
  productsLoading: false,
  sitesLoading: false,
};

const sidePanelSlice = createSlice({
  name: 'sidePanel',
  initialState,
  reducers: {
    // Sites flow actions
    openOrderSidePanel: (state, action: PayloadAction<{ site: any; orders: any[] }>) => {
      // Check if this is a different site selection (refresh scenario)
      const isDifferentSite = state.selectedSiteForOrders?.id !== action.payload.site?.id;
      
      state.showOrderSidePanel = true;
      state.selectedSiteForOrders = action.payload.site;
      state.siteOrders = action.payload.orders;
      
      // Always close child panels when opening or when site changes
      state.showProductSidePanel = false;
      state.showItemSidePanel = false;
      state.selectedOrderForProducts = null;
      state.selectedProduct = null;
    },
    
    openProductSidePanel: (state, action: PayloadAction<{ order: any; products: any[] }>) => {
      // Check if this is a different order selection (refresh scenario)
      const isDifferentOrder = state.selectedOrderForProducts?.id !== action.payload.order?.id;
      
      state.showProductSidePanel = true;
      state.selectedOrderForProducts = action.payload.order;
      state.orderProducts = action.payload.products;
      
      // Always close child panels when opening or when order changes
      state.showItemSidePanel = false;
      state.selectedProduct = null;
    },
    
    openItemSidePanel: (state, action: PayloadAction<{ product: any; order: any }>) => {
      state.showItemSidePanel = true;
      state.selectedProduct = action.payload.product;
      state.selectedOrder = action.payload.order;
    },
    
    // Customers flow actions
    openSiteSidePanel: (state, action: PayloadAction<{ customer: any; sites: any[] }>) => {
      // Check if this is a different customer selection (refresh scenario)
      const isDifferentCustomer = state.selectedCustomerForSites?.id !== action.payload.customer?.id;
      
      state.showSiteSidePanel = true;
      state.selectedCustomerForSites = action.payload.customer;
      state.customerSites = action.payload.sites;
      
      // Always close child panels when opening or when customer changes
      state.showOrderSidePanel = false;
      state.showProductSidePanel = false;
      state.showItemSidePanel = false;
      state.selectedSiteForOrders = null;
      state.selectedOrderForProducts = null;
      state.selectedProduct = null;
    },
    
    // Report flow actions
    openCustomerDetailsModal: (state, action: PayloadAction<{ customer: any; site: any; order: any }>) => {
      state.showCustomerDetailsModal = true;
      state.selectedCustomer = action.payload.customer;
      state.selectedSite = action.payload.site;
      state.selectedOrder = action.payload.order;
    },
    
    // Order details flow actions
    openItemPanel: (state, action: PayloadAction<{ product: any }>) => {
      state.showItemPanel = true;
      state.selectedProductForConfig = action.payload.product;
    },
    
    openItemSidePanelFromOrder: (state, action: PayloadAction<{ product: any }>) => {
      state.showItemSidePanel = true;
      state.selectedProductForItems = action.payload.product;
    },
    
    openItemStages: (state, action: PayloadAction<{ item: any }>) => {
      state.showItemStages = true;
      state.selectedItemForStages = action.payload.item;
    },
    
    // Back navigation actions
    backToSites: (state) => {
      state.showOrderSidePanel = false;
      state.selectedSiteForOrders = null;
      state.siteOrders = [];
    },
    
    backToOrders: (state) => {
      state.showProductSidePanel = false;
      state.selectedOrderForProducts = null;
      state.orderProducts = [];
      state.showOrderSidePanel = true;
    },
    
    backToProducts: (state) => {
      state.showItemSidePanel = false;
      state.selectedProduct = null;
      state.showProductSidePanel = true;
    },
    
    backToCustomers: (state) => {
      state.showSiteSidePanel = false;
      state.selectedCustomerForSites = null;
      state.customerSites = [];
    },
    
    backToSitesFromCustomer: (state) => {
      state.showOrderSidePanel = false;
      state.selectedSiteForOrders = null;
      state.siteOrders = [];
      state.showSiteSidePanel = true;
    },
    
    backToProductsFromOrder: (state) => {
      state.showItemSidePanel = false;
      state.selectedProductForItems = null;
      state.showItemPanel = true;
    },
    
    // Close all panels action
    closeAllPanels: (state) => {
      // Reset all panel states
      state.showOrderSidePanel = false;
      state.showProductSidePanel = false;
      state.showItemSidePanel = false;
      state.showSiteSidePanel = false;
      state.showCustomerDetailsModal = false;
      state.showItemPanel = false;
      state.showItemStages = false;
      
      // Reset all data states
      state.selectedSiteForOrders = null;
      state.selectedOrderForProducts = null;
      state.selectedProduct = null;
      state.selectedItem = null;
      state.selectedCustomerForSites = null;
      state.selectedProductForItems = null;
      state.selectedItemForStages = null;
      state.selectedOrder = null;
      state.selectedProductForConfig = null;
      
      // Reset data arrays
      state.siteOrders = [];
      state.orderProducts = [];
      state.customerSites = [];
      
      // Reset loading states
      state.ordersLoading = false;
      state.productsLoading = false;
      state.sitesLoading = false;
    },
    
    // Individual close actions (for back button functionality)
    closeOrderSidePanel: (state) => {
      state.showOrderSidePanel = false;
      state.selectedSiteForOrders = null;
      state.siteOrders = [];

      //close products
      state.showProductSidePanel = false;
      state.selectedOrderForProducts = null;
      state.orderProducts = [];

      //close items and stages
      state.showItemSidePanel = false;
      state.selectedProduct = null;
      state.selectedProductForItems = null;
      state.selectedOrder = null;

      state.showItemPanel = false;
      state.selectedProductForConfig = null;

      state.showItemStages = false;
      state.selectedItemForStages = null;
    },
    
    closeProductSidePanel: (state) => {
      state.showProductSidePanel = false;
      state.selectedOrderForProducts = null;
      state.orderProducts = [];

      // close items & stages that belong to product
      state.showItemSidePanel = false;
      state.selectedProduct = null;
      state.selectedProductForItems = null;
      state.selectedOrder = null;

      state.showItemPanel = false;
      state.selectedProductForConfig = null;

      state.showItemStages = false;
      state.selectedItemForStages = null;
    },
    
    closeItemSidePanel: (state) => {
      state.showItemSidePanel = false;
      state.selectedProduct = null;
      state.selectedOrder = null;

      // also close stages panel
      state.showItemStages = false;
      state.selectedItemForStages = null;
    },
    
    closeSiteSidePanel: (state) => {
      state.showSiteSidePanel = false;
      state.selectedCustomerForSites = null;
      state.customerSites = [];
      
      // close orders
      state.showOrderSidePanel = false;
      state.selectedSiteForOrders = null;
      state.siteOrders = [];

      // close products
      state.showProductSidePanel = false;
      state.selectedOrderForProducts = null;
      state.orderProducts = [];

      // close items & stages
      state.showItemSidePanel = false;
      state.selectedProduct = null;
      state.selectedProductForItems = null;
      state.showItemPanel = false;
      state.selectedProductForConfig = null;
      state.showItemStages = false;
      state.selectedItemForStages = null;
    },
    
    closeCustomerDetailsModal: (state) => {
      state.showCustomerDetailsModal = false;
      state.selectedCustomer = null;
      state.selectedSite = null;
      state.selectedOrder = null;
    },
    
    closeItemPanel: (state) => {
      state.showItemPanel = false;
      state.selectedProductForConfig = null;
    },
    
    closeItemStages: (state) => {
      state.showItemStages = false;
      state.selectedItemForStages = null;
    },
    
    // Loading state actions
    setOrdersLoading: (state, action: PayloadAction<boolean>) => {
      state.ordersLoading = action.payload;
    },
    
    setProductsLoading: (state, action: PayloadAction<boolean>) => {
      state.productsLoading = action.payload;
    },
    
    setSitesLoading: (state, action: PayloadAction<boolean>) => {
      state.sitesLoading = action.payload;
    },
    
    // Update product/item data when order updates (for real-time sync)
    updateProductForItems: (state, action: PayloadAction<{ product: any }>) => {
      if (state.showItemSidePanel && state.selectedProductForItems?.id === action.payload.product?.id) {
        state.selectedProductForItems = action.payload.product;
      }
    },
    
    updateItemForStages: (state, action: PayloadAction<{ item: any }>) => {
      if (state.showItemStages && state.selectedItemForStages?.id === action.payload.item?.id) {
        state.selectedItemForStages = action.payload.item;
      }
    },
  },
});

export const {
  // Open actions
  openOrderSidePanel,
  openProductSidePanel,
  openItemSidePanel,
  openSiteSidePanel,
  openCustomerDetailsModal,
  openItemPanel,
  openItemSidePanelFromOrder,
  openItemStages,
  
  // Back navigation actions
  backToSites,
  backToOrders,
  backToProducts,
  backToCustomers,
  backToSitesFromCustomer,
  backToProductsFromOrder,
  
  // Close actions
  closeAllPanels,
  closeOrderSidePanel,
  closeProductSidePanel,
  closeItemSidePanel,
  closeSiteSidePanel,
  closeCustomerDetailsModal,
  closeItemPanel,
  closeItemStages,
  
  // Loading actions
  setOrdersLoading,
  setProductsLoading,
  setSitesLoading,
  
  // Update actions for real-time sync
  updateProductForItems,
  updateItemForStages,
} = sidePanelSlice.actions;

export default sidePanelSlice.reducer;
